import React, { useState, useEffect, useCallback } from "react";
import {
	View,
	Text,
	TouchableOpacity,
	Platform,
	Alert,
	AppState
} from "react-native";
import { Icon } from "react-native-elements";
import { isMobile, isIOS, isAndroid } from "react-device-detect";
import { t } from "../services/i18n";
import { COLOR2, WHITE_COLOR, DARKGREY_COLOR } from "../styles/commonStyles";
import { openURL } from "../utils/UrlUtil";
import { APPLE_STORE_URL, GOOGLE_PLAY_URL } from "../config/constants";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useDispatch } from "react-redux";

const PREFER_STORE_ON_ANDROID = true;

const CompactInstallPrompt = () => {
	const [deferredPrompt, setDeferredPrompt] = useState(null);
	const [showPrompt, setShowPrompt] = useState(false);
	const [isInstalled, setIsInstalled] = useState(false);
	const [userAgent, setUserAgent] = useState("");
	const [promptDismissed, setPromptDismissed] = useState(false);
	const [promptCount, setPromptCount] = useState(0);
	const [lastPromptTime, setLastPromptTime] = useState(null);
	const [dataLoaded, setDataLoaded] = useState(false);

	const dispatch = useDispatch();

	const STORAGE_KEYS = {
		PROMPT_DISMISSED: "install_prompt_dismissed",
		PROMPT_COUNT: "install_prompt_count",
		LAST_PROMPT_TIME: "install_prompt_last_time"
	};

	const MAX_PROMPTS = 3;
	const PROMPT_COOLDOWN = 7 * 24 * 60 * 60 * 1000; // 7 jours en millisecondes

	useEffect(() => {
		initializeInstallPrompt();
		loadStoredData();
	}, []);

	const initializeInstallPrompt = useCallback(() => {
		setUserAgent(navigator.userAgent);
		checkIfInstalled();

		// Écouter l'événement beforeinstallprompt (Chrome/Edge)
		const handleBeforeInstallPrompt = e => {
			e.preventDefault();
			setDeferredPrompt(e);
			// La vérification sera faite par le useEffect après le chargement des données
		};

		// Écouter l'événement appinstalled
		const handleAppInstalled = () => {
			setIsInstalled(true);
			setShowPrompt(false);
			clearStoredData();
		};

		// Écouter les changements de display mode (PWA installée)
		const handleDisplayModeChange = e => {
			if (e.matches) {
				setIsInstalled(true);
				setShowPrompt(false);
				clearStoredData();
			}
		};

		// Écouter les changements d'état de l'app
		const handleAppStateChange = nextAppState => {
			if (nextAppState === "active") {
				checkIfInstalled();
			}
		};

		window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
		window.addEventListener("appinstalled", handleAppInstalled);

		// Utiliser addEventListener au lieu de addListener (déprécié)
		const mediaQuery = window.matchMedia("(display-mode: standalone)");
		mediaQuery.addEventListener("change", handleDisplayModeChange);

		AppState.addEventListener("change", handleAppStateChange);

		// Vérifier si on est déjà en mode standalone
		if (mediaQuery.matches) {
			setIsInstalled(true);
		}

		return () => {
			window.removeEventListener(
				"beforeinstallprompt",
				handleBeforeInstallPrompt
			);
			window.removeEventListener("appinstalled", handleAppInstalled);
			mediaQuery.removeEventListener("change", handleDisplayModeChange);
			AppState.removeEventListener("change", handleAppStateChange);
		};
	}, []);

	const loadStoredData = async () => {
		try {
			const [dismissed, count, lastTime] = await Promise.all([
				AsyncStorage.getItem(STORAGE_KEYS.PROMPT_DISMISSED),
				AsyncStorage.getItem(STORAGE_KEYS.PROMPT_COUNT),
				AsyncStorage.getItem(STORAGE_KEYS.LAST_PROMPT_TIME)
			]);

			setPromptDismissed(dismissed === "true");
			setPromptCount(parseInt(count) || 0);
			setLastPromptTime(parseInt(lastTime) || 0);
			setDataLoaded(true);
		} catch (error) {
			console.error("Error loading stored data:", error);
			setDataLoaded(true); // Même en cas d'erreur, on considère les données comme chargées
		}
	};

	const saveStoredData = async (key, value) => {
		try {
			await AsyncStorage.setItem(key, value.toString());
		} catch (error) {
			console.error("Error saving stored data:", error);
		}
	};

	const clearStoredData = async () => {
		try {
			await Promise.all([
				AsyncStorage.removeItem(STORAGE_KEYS.PROMPT_DISMISSED),
				AsyncStorage.removeItem(STORAGE_KEYS.PROMPT_COUNT),
				AsyncStorage.removeItem(STORAGE_KEYS.LAST_PROMPT_TIME)
			]);
		} catch (error) {
			console.error("Error clearing stored data:", error);
		}
	};

	const checkIfInstalled = () => {
		const isStandalone = window.matchMedia(
			"(display-mode: standalone)"
		).matches;
		const isInAppBrowser = /wv|WebView/.test(navigator.userAgent);
		const isFromHomeScreen = window.navigator.standalone === true;

		if (isStandalone || isInAppBrowser || isFromHomeScreen) {
			setIsInstalled(true);
			setShowPrompt(false);
		}
	};

	const shouldUseNativeSmartBanner = () => {
		// iOS Safari : utiliser le smart banner natif
		if (
			isIOS &&
			userAgent.includes("Safari") &&
			!userAgent.includes("Chrome")
		) {
			return true;
		}

		// Android Chrome : le support est limité, on garde notre solution
		// mais on pourrait l'activer dans le futur si le support s'améliore
		if (isAndroid && userAgent.includes("Chrome")) {
			// Pour l'instant, on garde notre solution personnalisée
			return false;
		}

		return false;
	};

	const checkShouldShowPrompt = useCallback(() => {
		// Ne pas vérifier si les données ne sont pas encore chargées
		if (!dataLoaded) {
			return;
		}

		if (isInstalled || promptDismissed) {
			return;
		}

		const now = Date.now();
		const timeSinceLastPrompt = now - lastPromptTime;

		// Ne pas afficher si on a atteint le maximum de prompts
		if (promptCount >= MAX_PROMPTS) {
			return;
		}

		// Ne pas afficher si on est dans la période de cooldown
		if (lastPromptTime && timeSinceLastPrompt < PROMPT_COOLDOWN) {
			return;
		}

		// Vérifier si les smart banners natifs sont disponibles
		if (shouldUseNativeSmartBanner()) {
			return;
		}

		// Ne pas afficher sur desktop sauf si c'est un navigateur supporté
		// Autoriser toutefois en développement ou si l'UA ressemble à un mobile (émulation)
		const uaLooksMobile = /iPhone|iPad|Android/i.test(userAgent);
		if (
			!isMobile &&
			!isIOS &&
			!isAndroid &&
			!uaLooksMobile &&
			!deferredPrompt &&
			process.env.NODE_ENV !== "development"
		) {
			return;
		}

		setShowPrompt(true);
	}, [
		dataLoaded,
		isInstalled,
		promptDismissed,
		lastPromptTime,
		promptCount,
		userAgent,
		deferredPrompt
	]);

	// Vérifier si on doit afficher la bannière après le chargement des données
	useEffect(() => {
		if (dataLoaded && deferredPrompt) {
			checkShouldShowPrompt();
		}
	}, [dataLoaded, deferredPrompt, checkShouldShowPrompt]);

	// En développement: si rien ne déclenche automatiquement l'affichage,
	// forcer un affichage après le montage pour faciliter les tests.
	useEffect(() => {
		if (process.env.NODE_ENV === "development" && dataLoaded) {
			setTimeout(() => {
				if (!isInstalled && !showPrompt && !promptDismissed) {
					setShowPrompt(true);
				}
			}, 500);
		}
	}, [isInstalled, showPrompt, dataLoaded, promptDismissed]);

	const handleInstallClick = async () => {
		// Sur Android, on préfère l'app native
		if (
			PREFER_STORE_ON_ANDROID &&
			(isAndroid || userAgent.includes("Android"))
		) {
			redirectToStore();
			setShowPrompt(false);
			setDeferredPrompt(null);
			return;
		}

		if (deferredPrompt) {
			// Chrome/Edge - utiliser l'API native
			try {
				deferredPrompt.prompt();
				const { outcome } = await deferredPrompt.userChoice;

				if (outcome === "accepted") {
					console.log("User accepted the install prompt");
					await saveStoredData(STORAGE_KEYS.PROMPT_COUNT, promptCount + 1);
					await saveStoredData(STORAGE_KEYS.LAST_PROMPT_TIME, Date.now());
				} else {
					console.log("User dismissed the install prompt");
					await saveStoredData(STORAGE_KEYS.PROMPT_COUNT, promptCount + 1);
					await saveStoredData(STORAGE_KEYS.LAST_PROMPT_TIME, Date.now());
				}
			} catch (error) {
				console.error("Error during install prompt:", error);
			}

			setDeferredPrompt(null);
			setShowPrompt(false);
		} else {
			// Autres navigateurs - rediriger vers les stores
			redirectToStore();
		}
	};

	const redirectToStore = () => {
		if (isIOS || userAgent.includes("iPhone") || userAgent.includes("iPad")) {
			openURL(dispatch, APPLE_STORE_URL);
		} else if (isAndroid || userAgent.includes("Android")) {
			openURL(dispatch, GOOGLE_PLAY_URL);
		} else {
			// Desktop ou autre - montrer les deux options
			Alert.alert(t("installApp.title"), t("installApp.choosePlatform"), [
				{ text: t("installApp.ios"), onPress: () => openURL(APPLE_STORE_URL) },
				{
					text: t("installApp.android"),
					onPress: () => openURL(dispatch, GOOGLE_PLAY_URL)
				},
				{ text: t("common.cancel"), style: "cancel" }
			]);
		}
	};

	const handleDismiss = async () => {
		setShowPrompt(false);
		setPromptDismissed(true);
		await saveStoredData(STORAGE_KEYS.PROMPT_DISMISSED, "true");
	};

	const handleLater = async () => {
		setShowPrompt(false);
		await saveStoredData(STORAGE_KEYS.PROMPT_COUNT, promptCount + 1);
		await saveStoredData(STORAGE_KEYS.LAST_PROMPT_TIME, Date.now());
	};

	// Ne pas afficher si déjà installée
	if (isInstalled) {
		return null;
	}

	// Ne pas afficher si pas de prompt et conditions non remplies
	if (!showPrompt) {
		return null;
	}

	return (
		<View style={styles.container}>
			<View style={styles.content}>
				<View style={styles.iconContainer}>
					<Icon
						name="cellphone"
						type="material-community"
						size={20}
						color={COLOR2}
					/>
				</View>

				<View style={styles.textContainer}>
					<Text style={styles.title}>{t("installApp:title")}</Text>
					<Text style={styles.description}>{t("installApp:hintText")}</Text>
				</View>

				<View style={styles.buttonContainer}>
					<TouchableOpacity
						onPress={handleInstallClick}
						style={styles.installButton}
					>
						<Text style={styles.installButtonText}>
							{t("installApp:installButton")}
						</Text>
					</TouchableOpacity>

					<TouchableOpacity
						onPress={handleDismiss}
						style={styles.dismissButton}
					>
						<Icon
							name="close"
							type="material-community"
							size={16}
							color={DARKGREY_COLOR}
						/>
					</TouchableOpacity>
				</View>
			</View>
		</View>
	);
};

const styles = {
	container: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		backgroundColor: WHITE_COLOR,
		borderTopWidth: 1,
		borderTopColor: "#e0e0e0",
		paddingHorizontal: 12,
		paddingVertical: 8,
		paddingBottom: Platform.OS === "ios" ? 20 : 8,
		zIndex: 1000,
		boxShadow: "0 -2px 8px rgba(0,0,0,0.1)"
	},
	content: {
		flexDirection: "row",
		alignItems: "center",
		width: "100%"
	},
	iconContainer: {
		marginRight: 8
	},
	textContainer: {
		flex: 1,
		marginRight: 8
	},
	title: {
		fontSize: 14,
		fontWeight: "bold",
		color: "#333",
		marginBottom: 2
	},
	description: {
		fontSize: 12,
		color: DARKGREY_COLOR,
		lineHeight: 16
	},
	buttonContainer: {
		flexDirection: "row",
		alignItems: "center"
	},
	installButton: {
		backgroundColor: COLOR2,
		borderRadius: 6,
		paddingHorizontal: 12,
		paddingVertical: 6,
		marginRight: 8
	},
	installButtonText: {
		fontSize: 12,
		fontWeight: "600",
		color: WHITE_COLOR
	},
	dismissButton: {
		padding: 4
	}
};

export default CompactInstallPrompt;
